// ipaCalculator.js
// ----------------------------------------------------------------------------
// PURPOSE
// Build IPA "requirements" (type/subtype/size/quantity) from UI inputs (rooms,
// selections). These requirements are later matched to products (ipa_products)
// by (type, subtype, size).
//
// CONVENTIONS & UNITS
// - Areas are in square meters (m²).
// - Pipe lengths are in meters (m).
// - Quantities for insulation surface items are expressed in m² (rounded to
//   the next full pack multiple), unless item is box-based (e.g., clips).
// - Rounding: see per-function notes (ceil/floor/round and “legacy parity”).
//
// KEY ENUMS (from IPAProducts):
// - IPA_PRODUCT_TYPES
// - INSULATION_VARIANTS (GROOVED, TACKER, DRY)
// - GROOVED_PANEL_SIZE (DIM_10MM, DIM_20MM, DIM_30MM, DIM_SYSTEM_REDUCED)
// - TACKER_PANEL_SIZE (THK_20, THK_25, THK_30, THK_50)
// - TACKER_FOIL (NONE, SIMPLE, ALUMINIUM)  -> UI selection
// - TACKER_FOIL_ROLL (SIMPLE_110, SIMPLE_55, ALU_50) -> distinct SKU sizes
// - INSULATION_CLIPS (GROOVED_CLIPS, TACKER_STAPLES)
// ----------------------------------------------------------------------------
import {
    IPA_PRODUCT_TYPES,
    INSULATION_VARIANTS,
    GROOVED_PANEL_SIZE,
    DISTRIBUTION_VARIANTS,
    INSULATION_CLIPS,
    TACKER_PANEL_SIZE,
    TACKER_FOIL,
    TACKER_FOIL_ROLL,
    PIPE_SIZE,
    ACCESSORY_VARIANTS,
    ACCESSORY_SIZE,
    DIST_ACCESSORY_VARIANTS,
    BOX_size,
    BOX_MAPPING,
} from "./IPAProducts";

/**
 * Choose distributor SKUs for a given total circuit count.
 *
 * Catalog limits:
 *  - MODULAR: single unit up to 16 circuits; 17–32 → two balanced units
 *    (ceil/floor); above 32 → floor(c/16) × 16 + remainder (rem=1 adjusted)
 *  - METALLIC/MONOLITIC: single unit up to 15 circuits; above → chop by 15
 *    (rem=1 adjusted). If not an exact catalog size and still ≤ 15 →
 *    split into ceil(c/2) and floor(c/2).
 *
 * Returns requirements by (type/subtype/size, quantity).
 */
export function createDistributionRequirements({
    distributionType,
    totalCircuits,
    rooms = [],
}) {
    if ((!distributionType || !totalCircuits) && !rooms?.length) return [];

    //  If rooms are provided, group by floor + apartment instead of summing all
    let groups = [];
    if (Array.isArray(rooms) && rooms.length > 0) {
        const grouped = {};
        rooms.forEach(r => {
            const floor = r.floor ?? 1;
            const apt = r.apartament ?? 0;
            const key = `${floor}_${apt}`;
            grouped[key] = (grouped[key] || 0) + (Number(r.circuit_no) || 0);
        });
        groups = Object.entries(grouped).map(([key, count]) => ({
            key,
            circuits: count,
        }));
    } else {
        groups = [{ key: "global", circuits: totalCircuits }];
    }

    const isModular =
        distributionType === DISTRIBUTION_VARIANTS.MODULAR_RR ||
        distributionType === DISTRIBUTION_VARIANTS.MODULAR_EC;

    const limit = isModular ? 16 : 15;
    const req = [];

    const pushBalancedPair = (push, c) => {
        const c1 = Math.ceil(c / 2);
        const c2 = Math.floor(c / 2);
        if (c1 === c2) push(c1, 2);
        else {
            push(c1, 1);
            push(c2, 1);
        }
    };

    // Internal helper for 1 group of circuits
    const processCircuits = circuits => {
        const bag = new Map();
        const push = (size, qty = 1) => {
            const key = `${distributionType}:${size}`;
            bag.set(key, (bag.get(key) || 0) + qty);
        };

        const c = circuits === 1 ? 2 : circuits;
        if (c > (isModular ? 32 : 30)) {
            let main = Math.floor(c / limit);
            let rem = c % limit;
            if (rem === 1 && main > 0) {
                if (main > 1) push(limit, main - 1);
                push(limit - 1, 1);
                push(2, 1);
            } else {
                if (main) push(limit, main);
                if (rem) push(rem, 1);
            }
        } else {
            const exact = c;
            const hasExact =
                (isModular && exact >= 2 && exact <= 16) ||
                (!isModular && exact >= 2 && exact <= 15);
            if (hasExact) {
                push(exact, 1);
            } else if (isModular) {
                // Modular 17–32: two balanced units (each ≤ 16), e.g. 28 → 2×14
                // instead of the old 12-based chop (12+12+4).
                pushBalancedPair(push, c);
            } else {
                const maxLegacy = 15;
                if (c > maxLegacy) {
                    let remaining = c;
                    while (remaining > maxLegacy) {
                        const step =
                            remaining - maxLegacy === 1
                                ? maxLegacy - 1
                                : maxLegacy;
                        push(step, 1);
                        remaining -= step;
                    }
                    if (remaining > 0) push(remaining, 1);
                } else {
                    pushBalancedPair(push, c);
                }
            }
        }

        for (const [key, quantity] of bag.entries()) {
            const [, sizeStr] = key.split(":");
            req.push({
                type: IPA_PRODUCT_TYPES.DISTRIBUTION,
                subtype: distributionType,
                size: Number(sizeStr),
                quantity,
            });
        }
    };

    // Process each group (floor/apartment)
    groups.forEach(({ circuits }) => {
        if (circuits > 0) {
            processCircuits(circuits);
        }
    });

    return req;
}

/**
 * Build main DISTRIBUTION products (Eurocon, Curbe, Robineti, etc.)
 * according to distributor type and pipe diameter.
 *
 * Rules based on the reference spreadsheet.
 */
export function createDistributionMainProductsRequirements({
    distributionType,
    pipeDiameter,
    distributionReqs = [],
}) {
    if (!distributionType || !distributionReqs.length) return [];

	// normalize PE-RT 16 to behave as 16mm; PE-Xa 17 (600m) to behave as 17mm
	if (pipeDiameter === 116) pipeDiameter = 16;
	if (pipeDiameter === 117) pipeDiameter = 17;
	// total circuite = suma size * quantity din distribuitoare
	const totalCircuits = distributionReqs.reduce(
		(sum, r) => sum + r.size * (r.quantity || 1),
		0
	);
	const numDistributors = distributionReqs.reduce(
		(sum, r) => sum + (r.quantity || 1),
		0
	);
	const req = [];
	const push = (subtype, size, qty) => {
		if (qty && qty > 0) {
			req.push({
				type: IPA_PRODUCT_TYPES.DISTRIBUTION_ACCESSORIES,
				subtype,
				size,
				quantity: qty,
			});
		}
	};

    // === METALLIC ===
    if (distributionType === DISTRIBUTION_VARIANTS.METALLIC) {
        if (pipeDiameter === 16)
            push(DIST_ACCESSORY_VARIANTS.EUROCON, 16, totalCircuits * 2);
        if (pipeDiameter === 17)
            push(DIST_ACCESSORY_VARIANTS.EUROCON, 17, totalCircuits * 2);
        if (pipeDiameter === 20)
            push(DIST_ACCESSORY_VARIANTS.EUROCON, 20, totalCircuits * 2);

        if (pipeDiameter <= 17)
            push(
                DIST_ACCESSORY_VARIANTS.CONDUCTIVE_CURVE,
                16,
                totalCircuits * 2,
            );
        else if (pipeDiameter === 20)
            push(
                DIST_ACCESSORY_VARIANTS.CONDUCTIVE_CURVE,
                20,
                totalCircuits * 2,
            );

        push(DIST_ACCESSORY_VARIANTS.VALVE, 1, numDistributors * 2);
    }

    // === MODULAR CU EUROCON ===
    if (distributionType === DISTRIBUTION_VARIANTS.MODULAR_EC) {
        if (pipeDiameter === 16)
            push(DIST_ACCESSORY_VARIANTS.EUROCON, 16, totalCircuits * 2);
        if (pipeDiameter === 17)
            push(DIST_ACCESSORY_VARIANTS.EUROCON, 17, totalCircuits * 2);
        if (pipeDiameter === 20)
            push(DIST_ACCESSORY_VARIANTS.EUROCON, 20, totalCircuits * 2);

        if (pipeDiameter <= 17)
            push(
                DIST_ACCESSORY_VARIANTS.CONDUCTIVE_CURVE,
                16,
                totalCircuits * 2,
            );
        else if (pipeDiameter === 20)
            push(
                DIST_ACCESSORY_VARIANTS.CONDUCTIVE_CURVE,
                20,
                totalCircuits * 2,
            );

        push(DIST_ACCESSORY_VARIANTS.REDUCTION, 1, numDistributors * 2);
        push(DIST_ACCESSORY_VARIANTS.VALVE_RR, 1, numDistributors * 2);
        // push(DIST_ACCESSORY_VARIANTS.MODULE, 1, numDistributors * 2); // 302.05.01
        // push(DIST_ACCESSORY_VARIANTS.MODULE, 3, numDistributors * 2); // 302.07.01
    }

    // === MODULAR CU RACORD RAPID ===
    if (distributionType === DISTRIBUTION_VARIANTS.MODULAR_RR) {
        if (pipeDiameter === 16)
            push(
                DIST_ACCESSORY_VARIANTS.RAPID_CONNECTOR,
                16,
                totalCircuits * 2,
            );
        if (pipeDiameter === 17)
            push(
                DIST_ACCESSORY_VARIANTS.RAPID_CONNECTOR,
                17,
                totalCircuits * 2,
            );

        if (pipeDiameter <= 17)
            push(
                DIST_ACCESSORY_VARIANTS.CONDUCTIVE_CURVE,
                16,
                totalCircuits * 2,
            );
        else if (pipeDiameter === 20)
            push(
                DIST_ACCESSORY_VARIANTS.CONDUCTIVE_CURVE,
                20,
                totalCircuits * 2,
            );

        push(DIST_ACCESSORY_VARIANTS.REDUCTION, 1, numDistributors * 2);
        push(DIST_ACCESSORY_VARIANTS.VALVE_RR, 1, numDistributors * 2);
        push(DIST_ACCESSORY_VARIANTS.MODULE, 4, numDistributors * 2); // 317.05.54
        push(DIST_ACCESSORY_VARIANTS.MODULE, 3, numDistributors * 2); // 302.07.01
    }

    return req;
}

/**
 * Summarize distribution accessories counters like the legacy code:
 *  - inlet            += distribution_code * quantity
 *  - conductiveCurves += distribution_code * quantity
 *  - pump_25_60: distribution_code < 13
 *  - pump_25_70: distribution_code >= 13
 */
export function summarizeDistribution({ distributionReqs }) {
    let inlet = 0;
    let pump_25_60 = 0;
    let pump_25_70 = 0;
    let conductiveCurves = 0;

    distributionReqs.forEach(r => {
        if (r.type !== IPA_PRODUCT_TYPES.DISTRIBUTION) return;
        const code = Number(r.size) || 0;
        const q = Number(r.quantity) || 0;
        inlet += code * q;
        conductiveCurves += code * q;
        if (code < 13) pump_25_60 += q;
        else pump_25_70 += q;
    });

    return { inlet, pump_25_60, pump_25_70, conductiveCurves };
}

// ============================================================================
// TEMPERATURE CONTROL
// ============================================================================

export function createTemperatureControlRequirements({
    tempControlKit,
    numDistributors,
}) {
    if (!tempControlKit || tempControlKit === 1) return [];

    let requirements = [];
    const add = (type, subtype, qty) =>
        qty > 0 && requirements.push({ type, subtype, quantity: qty });

    // un kit per distribuitor
    const numKits = numDistributors || 1;

    switch (tempControlKit) {
        case 2: // Vana 3 cai 25-60
            add(IPA_PRODUCT_TYPES.TEMPERATURE_CONTROL, 2, numKits);
            break;
        case 3: // Vana 3 cai 25-70
            add(IPA_PRODUCT_TYPES.TEMPERATURE_CONTROL, 3, numKits);
            break;
        case 4: // Vana 3 cai 25-80
            add(IPA_PRODUCT_TYPES.TEMPERATURE_CONTROL, 4, numKits);
            break;
        case 5: // Vana 4 cai 25-60
            add(IPA_PRODUCT_TYPES.TEMPERATURE_CONTROL, 5, numKits);
            break;
        case 6: // Vana 4 cai 25-70
            add(IPA_PRODUCT_TYPES.TEMPERATURE_CONTROL, 6, numKits);
            break;
        case 7: // Vana 4 cai 25-80
            add(IPA_PRODUCT_TYPES.TEMPERATURE_CONTROL, 7, numKits);
            break;
    }

    // Adăugăm robineții speciali (număr de kituri × 2)
    if (tempControlKit >= 2 && tempControlKit <= 4) {
        // vane cu 3 cai -> robinet semiolandez
        add(
            IPA_PRODUCT_TYPES.DISTRIBUTION_ACCESSORIES,
            DIST_ACCESSORY_VARIANTS.VALVE_TEMP_SEMIOLANDEZ,
            numKits * 2,
        );
    } else if (tempControlKit >= 5 && tempControlKit <= 7) {
        // vane cu 4 cai -> robinet cu olandez
        add(
            IPA_PRODUCT_TYPES.DISTRIBUTION_ACCESSORIES,
            DIST_ACCESSORY_VARIANTS.VALVE_TEMP_OLANDEZ,
            numKits * 2,
        );
    }

    return requirements;
}

// ============================================================================
// GROOVED INSULATION
// ============================================================================

/**
 * Build GROOVED panel requirements per size.
 *
 * Rule:
 *   qty(m²) = ceil(area/pack)*pack + ceil((area/pack) * WASTE_RATIO) * pack
 *
 * Defaults (packs in m²/pack):
 *   - 10mm  : 0.85
 *   - 20mm  : 0.85
 *   - 30mm  : 0.85
 *   - system reduced: 1.12
 *
 * Legacy delta (explicitly kept):
 *   - Old IPA used 2% waste; here we use 3% (WASTE_RATIO = 0.03).
 *   - Old pack for 10mm was 1.12; here is 0.85.
 *
 * @param {Object} params
 * @param {Array} params.rooms
 * @param {Object<number,number>} params.packingBySize - m² per pack by size enum
 * @returns {Array<{type,subtype,size,quantity}>}
 */
export function createGroovedInsulationRequirements({
    rooms,
    packingBySize = {
        //packing size necessary for 101.01.102, 101.02.102, 101.03.102
        //Only when is on stock from warehouse
        // [GROOVED_PANEL_SIZE.DIM_10MM]: 0.85,
        // [GROOVED_PANEL_SIZE.DIM_20MM]: 0.85,
        // [GROOVED_PANEL_SIZE.DIM_30MM]: 0.85,

        //packing size necessary for 101.01.51, 101.02.51, 101.03.51
        //Only when *102 is not on stock from warehouse
        [GROOVED_PANEL_SIZE.DIM_10MM]: 1.12,
        [GROOVED_PANEL_SIZE.DIM_20MM]: 1.12,
        [GROOVED_PANEL_SIZE.DIM_30MM]: 1.12,
        [GROOVED_PANEL_SIZE.DIM_SYSTEM_REDUCED]: 1.12,
    },
}) {
    if (!Array.isArray(rooms) || rooms.length === 0) {
        return [];
    }
    // Aggregate raw area by GROOVED size (m²)
    const areaBySize = {};
    rooms.forEach(room => {
        const ins =
            room?.insulationType ??
            room?.insulation_type ??
            room?.insulationType?.value ??
            room?.insulation_type?.value;
        const area =
            typeof room?.area === "number"
                ? room.area
                : Number(room?.area?.sq_m || 0);

        if (!ins) return;
        if (ins.subtype !== INSULATION_VARIANTS.GROOVED) return; // only grooved
        if (!area) return;

        const size = ins.size;
        areaBySize[size] = (areaBySize[size] || 0) + area;
    });

    // Convert each bucket to final ordered m² (multiple of packs, incl. waste)
    let requirements = [];
    Object.entries(areaBySize).forEach(([sizeStr, area]) => {
        const size = Number(sizeStr);
        const pack = packingBySize[size] || 0;
        if (!pack) {
            return;
        }
        // Return raw area (no waste, no rounding) - rounding with waste happens in roundRequirements
        requirements.push({
            type: IPA_PRODUCT_TYPES.INSULATION,
            subtype: INSULATION_VARIANTS.GROOVED,
            size,
            quantity: area,
        });
    });

    return requirements;
}

/**
 * GROOVED clips (legacy parity).
 *
 * Rule (original Python):
 *   qty(boxes) = ceil( grooved_total_area / 100 )
 *   where grooved_total_area is the sum of room areas with grooved insulation
 *
 * Now also adds:
 *   1 clip per 50 m² roll of reinforcement mesh
 *
 * Note:
 * - The product for GROOVED_CLIPS should have UM = "box".
 * - Uses the grooved insulation requirements quantities (which are raw room areas)
 */
export function createGroovedClipsRequirements(
    { rooms, groovedInsulation },
    reinforcementMesh,
) {
    // Calculate grooved total area (sum of room areas with grooved insulation)
    // Use grooved insulation requirements if provided (their quantity is the raw room area)
    // Otherwise calculate from rooms directly
    let groovedTotalArea = 0;

    if (
        groovedInsulation &&
        Array.isArray(groovedInsulation) &&
        groovedInsulation.length > 0
    ) {
        // Sum the quantities from grooved insulation requirements
        // These quantities are the raw room areas (before rounding/waste)

        groovedTotalArea = groovedInsulation.reduce(
            (sum, req) => sum + (req.quantity || 0),
            0,
        );
    } else if (rooms) {
        // Fallback: calculate from room areas directly

        rooms.forEach(r => {
            const ins =
                r?.insulationType ??
                r?.insulation_type ??
                r?.insulationType?.value ??
                r?.insulation_type?.value;
            const subtype = Number(ins?.subtype ?? ins?.value?.subtype);

            const area =
                typeof r?.area === "number"
                    ? r.area
                    : Number(r?.area?.sq_m || 0);

            if (subtype === INSULATION_VARIANTS.GROOVED) {
                groovedTotalArea += area || 0;
            }
        });
    }

    // 1 clip per 100 m² of grooved area (raw, before rounding)
    // Note: Rounding to ceil happens in roundRequirements, here we return raw
    const groovedClipsQty = groovedTotalArea > 0 ? groovedTotalArea / 100 : 0;

    // 1 clip per 50 m² roll of reinforcement mesh
    // Mesh should already be rounded when passed from roundRequirements
    // Sum the rounded mesh quantities to get total rounded mesh area
    const hasGrooved = groovedTotalArea > 0;

    const totalMeshArea = hasGrooved
        ? reinforcementMesh?.reduce(
              (acc, curr) => acc + (curr.quantity || 0),
              0,
          ) || 0
        : 0;

    const meshClipsQty =
        hasGrooved && totalMeshArea > 0 ? totalMeshArea / 50 : 0;

    const totalQty = groovedClipsQty + meshClipsQty;

    if (totalQty <= 0) return [];

    return [
        {
            type: IPA_PRODUCT_TYPES.INSULATION,
            subtype: INSULATION_VARIANTS.GROOVED,
            size: INSULATION_CLIPS.GROOVED_CLIPS,
            quantity: totalQty,
        },
    ];
}

/**
 * Build DRY insulation requirements (EPS - Sistem uscat)
 *
 * Rule (legacy parity):
 *   qty(m²) = ceil(area / pack) * pack
 *   qty_final = ceil((qty + qty * 0.02) / pack) * pack
 *
 * Default pack coverage: 1.00 m²/pack (adjust if needed)
 *
 * @param {Object} params
 * @param {Array} params.rooms
 * @param {Object<number, number>} params.packingBySubtype - m² per pack (optional)
 * @returns {Array<{type,subtype,size,quantity}>}
 */
export function createDryInsulationRequirements({ rooms }) {
    if (!Array.isArray(rooms) || rooms.length === 0) return [];

    let totalArea = 0;
    rooms.forEach(room => {
        const ins =
            room?.insulationType ??
            room?.insulation_type ??
            room?.insulationType?.value ??
            room?.insulation_type?.value;

        const area =
            typeof room?.area === "number"
                ? room.area
                : Number(room?.area?.sq_m || 0);
        if (ins?.subtype === INSULATION_VARIANTS.DRY) {
            totalArea += area || 0;
        }
    });

    if (!totalArea) return [];

    // Return raw area in m² (waste and rounding happens in roundRequirements)
    return [
        {
            type: IPA_PRODUCT_TYPES.INSULATION,
            subtype: INSULATION_VARIANTS.DRY,
            size: 1,
            quantity: totalArea,
        },
    ];
}

// ============================================================================
// TACKER PANELS & ROLLS (surface insulation, expressed in m²)
// ============================================================================

/**
 * TACKER PLATES by thickness (excludes rooms that opted for "roll" mode).
 *
 * Rule per thickness:
 *   qty(m²) = ceil(area / pack_m2) * pack_m2
 *
 * Defaults (pack m² by thickness):
 *   - 20mm: 12.5
 *   - 25mm: 10   (treated as “roll” thickness if you use panel size for rolls)
 *   - 30mm: 8
 *   - 50mm: 5
 *
 * Gate:
 *   - Skip room when `tackerFoil` or `tackerRoll` is set (those will be handled
 *     by roll/foil functions).
 */
export function createTackerPlateRequirements({
    rooms,
    packByThickness = {
        [TACKER_PANEL_SIZE.THK_20]: 12.5,
        [TACKER_PANEL_SIZE.THK_25]: 10, //  ROLĂ
        [TACKER_PANEL_SIZE.THK_30]: 8,
        [TACKER_PANEL_SIZE.THK_50]: 5,
    },
}) {
    if (!Array.isArray(rooms) || !rooms.length) {
        return [];
    }

    // Aggregate TACKER area by thickness, excluding “roll” rooms
    const areaByThickness = {};
    rooms.forEach(r => {
        const ins =
            r?.insulationType ??
            r?.insulation_type ??
            r?.insulationType?.value ??
            r?.insulation_type?.value;

        const area =
            typeof r?.area === "number" ? r.area : Number(r?.area?.sq_m || 0);
        if (!ins || ins.subtype !== INSULATION_VARIANTS.TACKER) return;
        //  Now only skip if tackerRoll is true, not if foil is selected
        const isRoll = !!r?.tackerRoll;
        if (isRoll) return;
        if (!area) return;

        const size = Number(ins.size);
        areaByThickness[size] = (areaByThickness[size] || 0) + area;
    });

    // Convert each bucket to ordered m² (pack multiples)
    const out = [];
    Object.entries(areaByThickness).forEach(([sizeStr, area]) => {
        const size = Number(sizeStr);
        const pack = packByThickness[size] || 0;
        if (!pack) {
            return;
        }
        const qtyM2 = area; // Return raw area (rounding happens in roundRequirements)

        out.push({
            type: IPA_PRODUCT_TYPES.INSULATION,
            subtype: INSULATION_VARIANTS.TACKER,
            size, // THK_20/30/50
            quantity: qtyM2, // m² (multiplu or raw)
        });
    });

    return out;
}

/**
 * TACKER ROLL 25mm (surface mode expressed via panel size THK_25).
 *
 * Rule:
 *   qty(m²) = ceil(total_roll_area / 10) * 10
 *
 * Gate:
 *   - A room is considered “roll” if it sets `tackerFoil` (SIMPLE/ALUMINIUM)
 *     or a dedicated `tackerRoll` flag (if present).
 */
export function createTackerRollRequirements({
    rooms,
    rollSizeEnum = TACKER_PANEL_SIZE.THK_25,
}) {
    if (!Array.isArray(rooms) || !rooms.length) {
        return [];
    }

    let totalArea = 0;
    let hasRoll = false;
    rooms.forEach(r => {
        const ins =
            r?.insulationType ??
            r?.insulation_type ??
            r?.insulationType?.value ??
            r?.insulation_type?.value;

        const area =
            typeof r?.area === "number" ? r.area : Number(r?.area?.sq_m || 0);
        if (!ins || ins.subtype !== INSULATION_VARIANTS.TACKER) return;
        if (r?.tackerRoll) {
            hasRoll = true;
            totalArea += area || 0;
        }
    });

    if (!hasRoll || !totalArea) {
        return [];
    }

    const qtyM2 = totalArea; // Return raw area (rounding happens in roundRequirements)

    return [
        {
            type: IPA_PRODUCT_TYPES.INSULATION,
            subtype: INSULATION_VARIANTS.TACKER,
            size: rollSizeEnum, // THK_25
            quantity: qtyM2, // m² multiplu de 10
        },
    ];
}

// ============================================================================
// TACKER CLIPS (box-based quantity)
// ============================================================================

/**
 * Build TACKER clips requirements (Clips Tacker 40mm).
 *
 * Rule (legacy parity):
 *   pieces = round(total_pipe_m * 3.5)
 *   boxes  = ceil(pieces / BOX_SIZE)
 *
 * Notes:
 * - Only add clips if there is any TACKER insulation on the offer.
 * - Default BOX_SIZE is 300 pcs/box (aligns with old program).
 * - If price list exposes packing for SKU 106.02.06, pass it as boxSize here.
 *
 * @param {Object} params
 * @param {Array} params.rooms
 * @param {boolean} params.hasTackerInsulation - gate to add clips
 * @param {number} [params.staplesPerMeter=3.5]
 * @param {number} [params.boxSize=300] - pcs per box
 * @param {number} [params.staplesEnum=INSULATION_CLIPS.TACKER_STAPLES]
 * @returns {Array<{type,subtype,size,quantity}>}
 */
export function createTackerClipsRequirements(
    {
        rooms,
        hasTackerInsulation = false,
        staplesPerMeter = 3.5,
        boxSize = 300,
        staplesEnum = INSULATION_CLIPS.TACKER_STAPLES,
    },
    reinforcementMesh,
) {
    if (!hasTackerInsulation) {
        return [];
    }

    let totalPipe = 0;
    rooms?.forEach(r => {
        totalPipe += Number(r?.total_pipe || 0);
    });

    const reinforcementMeshQty = Math.ceil(
        reinforcementMesh.reduce((acc, curr) => curr.quantity, 0) / 100,
    );

    // Return raw quantities (rounding happens in roundRequirements)
    const staples = totalPipe * staplesPerMeter;
    const boxes = staples / boxSize;

    if (!boxes) return [];
    return [
        {
            type: IPA_PRODUCT_TYPES.INSULATION,
            subtype: INSULATION_VARIANTS.TACKER,
            size: staplesEnum,
            quantity: boxes + reinforcementMeshQty,
        },
    ];
}
// ============================================================================
// TACKER FOIL (SKU-level: SIMPLE 110/55, ALU 50). Quantities expressed in m².
// ============================================================================

/**
 * Build TACKER FOIL requirements with **legacy-compatible** rules.
 *
 * Inputs:
 *   - Aggregate all rooms with subtype=TACKER and a `tackerFoil` selection.
 *   - SIMPLE and ALUMINIUM areas are handled separately.
 *
 * Rules (legacy parity):
 *   SIMPLE:
 *     let A = sum(simpleArea);
 *     t55 = ceil(A / 55) * 55;
 *     if t55 < 55:
 *       order 55
 *     else if t55 % 110 === 55:
 *       order floor(t55/110)*110  (SIMPLE_110)
 *       +      55                 (SIMPLE_55)
 *     else:
 *       order ceil(t55/110)*110   (SIMPLE_110)
 *
 *   ALU:
 *     order ceil( aluArea / 50 ) * 50 (ALU_50)
 */
export function createTackerFoilRequirementsOld({ rooms }) {
    let simpleArea = 0;
    let aluArea = 0;
    const isTrue = v => {
        const x = v && typeof v === "object" && "value" in v ? v.value : v;
        return x === true || x === 1 || x === "1" || x === "true";
    };

    const getRollFlag = (r, ins) => {
        const insSize = Number(ins?.size ?? ins?.value?.size ?? 0);
        return (
            isTrue(r?.tackerRoll) ||
            isTrue(r?.tacker_roll) ||
            isTrue(r?.insulationType?.tackerRoll) ||
            isTrue(r?.insulationType?.value?.tackerRoll) ||
            isTrue(r?.location?.tackerRoll) ||
            // Treat THK_25 as roll-mode even if flag is missing
            insSize === Number(TACKER_PANEL_SIZE.THK_25)
        );
    };

    rooms?.forEach(r => {
        const ins =
            r?.insulationType ??
            r?.insulation_type ??
            r?.insulationType?.value ??
            r?.insulation_type?.value;

        if (!ins || ins.subtype !== INSULATION_VARIANTS.TACKER) return;
        const isRoll = getRollFlag(r, ins);
        if (isRoll) return;
        const area =
            typeof r?.area === "number" ? r.area : Number(r?.area?.sq_m || 0);
        if (!area) return;

        const foilType = isRoll
            ? TACKER_FOIL.NONE
            : r?.tackerFoil?.value ?? r?.tackerFoil ?? TACKER_FOIL.NONE;
        if (foilType === TACKER_FOIL.SIMPLE) simpleArea += area;
        else if (foilType === TACKER_FOIL.ALUMINIUM) aluArea += area;
    });

    const req = [];

    // SIMPLE → return raw area (rounding happens in roundRequirements)
    if (simpleArea > 0) {
        req.push({
            type: IPA_PRODUCT_TYPES.INSULATION,
            subtype: INSULATION_VARIANTS.TACKER,
            size: TACKER_FOIL_ROLL.SIMPLE_110,
            quantity: simpleArea,
        });
    }

    // ALU → return raw area (rounding happens in roundRequirements)
    if (aluArea > 0) {
        req.push({
            type: IPA_PRODUCT_TYPES.INSULATION,
            subtype: INSULATION_VARIANTS.TACKER,
            size: TACKER_FOIL_ROLL.ALU_50,
            quantity: aluArea,
        });
    }
    return req;
}
export function createAutomationRequirements({
    automation, // 2 = cu fir, 3 = cu RF
    circuitCount,
    numDistributors,
}) {
    if (!automation || automation === 1) return [];

    const req = [];
    const add = (subtype, size, qty) => {
        req.push({
            type: IPA_PRODUCT_TYPES.AUTOMATION,
            subtype,
            size,
            quantity: qty ?? 0,
        });
    };

    // === COMUNE ===
    add(1, 1, circuitCount); // Servomotor 230V - normal închis
    // add(1, 4, 0); //Termostat cu receiver si Wi-Fi
    // add(1, 7, 0);
    // add(1, 5, 0);
    // add(1, 6, 0);
    // add(1, 8, 0);
    // === CU FIR ===
    if (automation === 2) {
        add(2, 6, numDistributors); // Cutie de comandă cu 8 zone 230V
        // add(2, 1, 0); // Termostat mecanic
        // add(2, 2, 0); // Termostat digital
        // add(2, 3, 0); // Termostat BF ZigBee
        // add(2, 4, 0); // Touchscreen cu fir
        // add(2, 5, 0); // Touchscreen Wi-Fi
    }

    // === CU RF ===
    if (automation === 3) {
        add(3, 1, numDistributors); // Cutie comandă ZigBee
        // add(3, 2, 0); // Termostat digital RF
        // add(3, 3, 0); // Receiver 868 MHz
        add(1, 3, 1); // Gateway (releu inteligent / cutie) – 1 per unit when automation = cu RF
    }
    return req;
}
/**
 * Builds reinforcement mesh product requirements.
 *
 * @param {Object} params - Input parameters.
 * @param {Array<Object>} params.rooms - List of room objects containing area data.
 * @param {number} [params.reinforcementMesh=0] - Flag from form (0 = No, 1 = Yes).
 * @returns {Array<Object>} A list of product requirement objects.
 *
 * @description
 * - Mirrors the original IPA logic:
 *   If the mesh option is enabled (“Da”), add product `503.01.13` (“Plasă din fibră de sticlă”).
 * - The total quantity is calculated as:
 *   `ceil(totalArea / packing) * packing`
 *   where `packing = 50 m² / pack`.
 * - If not enabled, returns an empty array.
 */
export function createReinforcementMeshRequirements({
    rooms,
    reinforcementMesh = 0, // 0 = No, 1 = Yes
}) {
    // --- 1) Validate inputs
    if (!Array.isArray(rooms) || rooms.length === 0) {
        return [];
    }

    // --- 2) Skip if mesh option is disabled
    if (reinforcementMesh !== 1) {
        return [];
    }

    // --- 3) Compute total floor area (m²)
    const totalArea = rooms.reduce((sum, r) => {
        const area =
            typeof r?.area === "number" ? r.area : Number(r?.area?.sq_m || 0);
        return sum + (area || 0);
    }, 0);

    if (!totalArea) {
        return [];
    }

    // --- 4) Return raw area (rounding happens in roundRequirements)
    const qtyM2 = totalArea;

    // --- 5) Build requirement
    const req = [
        {
            type: IPA_PRODUCT_TYPES.REINFORCEMENT_MESH,
            subtype: 1,
            size: 50,
            quantity: qtyM2,
        },
    ];

    return req;
}

// ============================================================================
// DISTRIBUTION BOX (unchanged)
// ============================================================================

/**
 * Build BOX requirements.
 *
 * Box selection depends on:
 *  - Distributor type: metallic / modular
 *  - Temperature control: with or without kit
 *  - Automation: with or without automation
 *  - Box subtype: SISTEMA / SP
 *  - Number of circuits
 *
 * Each group (floor/apartment) gets one box.
 * The result matches ipaProducts by (type, subtype, size).
 */
/**
 * Build BOX requirements using full Excel logic.
 * Depends on: distributor type, tempControlKit, automation, subtype, and circuit count.
 *
 * The logic directly matches ipaProducts by (type, subtype, size).
 */
// In ipaCalculator.js

export function createBoxesRequirements({
    distributionType,
    tempControlKit,
    metalBox,
    distributionReqs = [],
}) {
    if (!distributionReqs.length) {
        return [];
    }
    // If metalBox is set to "NU", don't create any boxes
    if (Number(metalBox) === BOX_MAPPING.NU) {
        return [];
    }

    // Correct modular detection (handles both RR and EC)
    const isModular =
        distributionType === DISTRIBUTION_VARIANTS.MODULAR_RR ||
        distributionType === DISTRIBUTION_VARIANTS.MODULAR_EC;

    const isKit = tempControlKit > 1;

    // SISTEMA or SP system
    const boxSystem = metalBox === BOX_MAPPING.SISTEMA ? "SISTEMA" : "SP";

    // Mode key: always use AUTO or KIT_AUTO (never BASE/KIT)
    const mode = isKit ? "KIT_AUTO" : "AUTO";

    // ----------------------------------------------------------------------------
    // FULL EXCEL MAPPING FOR ALL TYPES
    // ----------------------------------------------------------------------------
    const BOX_MAP = {
        METALLIC: {
            SISTEMA: {
                BASE: {
                    2: BOX_size.DIM_600x620x110,
                    3: BOX_size.DIM_600x620x110,
                    4: BOX_size.DIM_600x620x110,
                    5: BOX_size.DIM_600x620x110,
                    6: BOX_size.DIM_600x620x110,
                    7: BOX_size.DIM_600x620x110,
                    8: BOX_size.DIM_600x620x110,
                    9: BOX_size.DIM_800x620x110,
                    10: BOX_size.DIM_800x620x110,
                    11: BOX_size.DIM_800x620x110,
                    12: BOX_size.DIM_800x620x110,
                    13: BOX_size.DIM_1000x620x110,
                    14: BOX_size.DIM_1000x620x110,
                    15: BOX_size.DIM_1000x620x110,
                },
                KIT: {
                    2: BOX_size.DIM_600x620x110,
                    3: BOX_size.DIM_600x620x110,
                    4: BOX_size.DIM_600x620x110,
                    5: BOX_size.DIM_600x620x110,
                    6: BOX_size.DIM_600x620x110,
                    7: BOX_size.DIM_600x620x110,
                    8: BOX_size.DIM_800x620x110,
                    9: BOX_size.DIM_800x620x110,
                    10: BOX_size.DIM_800x620x110,
                    11: BOX_size.DIM_1000x620x110,
                    12: BOX_size.DIM_1000x620x110,
                    13: BOX_size.DIM_1000x620x110,
                    14: BOX_size.DIM_1000x620x110,
                    15: BOX_size.DIM_1200x620x110,
                },
                AUTO: {
                    2: BOX_size.DIM_600x620x110,
                    3: BOX_size.DIM_600x620x110,
                    4: BOX_size.DIM_600x620x110,
                    5: BOX_size.DIM_600x620x110,
                    6: BOX_size.DIM_800x620x110,
                    7: BOX_size.DIM_800x620x110,
                    8: BOX_size.DIM_800x620x110,
                    9: BOX_size.DIM_800x620x110,
                    10: BOX_size.DIM_1000x620x110,
                    11: BOX_size.DIM_1000x620x110,
                    12: BOX_size.DIM_1000x620x110,
                    13: BOX_size.DIM_1000x620x110,
                    14: BOX_size.DIM_1200x620x110,
                    15: BOX_size.DIM_1200x620x110,
                },
                KIT_AUTO: {
                    2: BOX_size.DIM_600x620x110,
                    3: BOX_size.DIM_600x620x110,
                    4: BOX_size.DIM_800x620x110,
                    5: BOX_size.DIM_800x620x110,
                    6: BOX_size.DIM_800x620x110,
                    7: BOX_size.DIM_800x620x110,
                    8: BOX_size.DIM_1000x620x110,
                    9: BOX_size.DIM_1000x620x110,
                    10: BOX_size.DIM_1000x620x110,
                    11: BOX_size.DIM_1000x620x110,
                    12: BOX_size.DIM_1200x620x110,
                    13: BOX_size.DIM_1200x620x110,
                    14: BOX_size.DIM_1200x620x110,
                    15: BOX_size.DIM_1200x620x110,
                },
            },
            SP: {
                BASE: {
                    2: BOX_size.DIM_340x580x110,
                    3: BOX_size.DIM_440x580x110,
                    4: BOX_size.DIM_440x580x110,
                    5: BOX_size.DIM_580x580x110,
                    6: BOX_size.DIM_580x580x110,
                    7: BOX_size.DIM_580x580x110,
                    8: BOX_size.DIM_720x580x110,
                    9: BOX_size.DIM_720x580x110,
                    10: BOX_size.DIM_720x580x110,
                    11: BOX_size.DIM_780x580x110,
                    12: BOX_size.DIM_780x580x110,
                    13: BOX_size.DIM_970x580x110,
                    14: BOX_size.DIM_970x580x110,
                    15: BOX_size.DIM_970x580x110,
                },
                KIT: {
                    2: BOX_size.DIM_440x580x110,
                    3: BOX_size.DIM_580x580x110,
                    4: BOX_size.DIM_580x580x110,
                    5: BOX_size.DIM_580x580x110,
                    6: BOX_size.DIM_720x580x110,
                    7: BOX_size.DIM_720x580x110,
                    8: BOX_size.DIM_720x580x110,
                    9: BOX_size.DIM_780x580x110,
                    10: BOX_size.DIM_780x580x110,
                    11: BOX_size.DIM_970x580x110,
                    12: BOX_size.DIM_970x580x110,
                    13: BOX_size.DIM_970x580x110,
                    14: BOX_size.DIM_1145x580x110,
                    15: BOX_size.DIM_1145x580x110,
                },
                AUTO: {
                    2: BOX_size.DIM_580x580x110,
                    3: BOX_size.DIM_580x580x110,
                    4: BOX_size.DIM_580x580x110,
                    5: BOX_size.DIM_720x580x110,
                    6: BOX_size.DIM_720x580x110,
                    7: BOX_size.DIM_720x580x110,
                    8: BOX_size.DIM_780x580x110,
                    9: BOX_size.DIM_780x580x110,
                    10: BOX_size.DIM_970x580x110,
                    11: BOX_size.DIM_970x580x110,
                    12: BOX_size.DIM_970x580x110,
                    13: BOX_size.DIM_1145x580x110,
                    14: BOX_size.DIM_1145x580x110,
                    15: BOX_size.DIM_1345x580x110,
                },
                KIT_AUTO: {
                    2: BOX_size.DIM_580x580x110,
                    3: BOX_size.DIM_720x580x110,
                    4: BOX_size.DIM_720x580x110,
                    5: BOX_size.DIM_720x580x110,
                    6: BOX_size.DIM_780x580x110,
                    7: BOX_size.DIM_780x580x110,
                    8: BOX_size.DIM_970x580x110,
                    9: BOX_size.DIM_970x580x110,
                    10: BOX_size.DIM_970x580x110,
                    11: BOX_size.DIM_1145x580x110,
                    12: BOX_size.DIM_1145x580x110,
                    13: BOX_size.DIM_1145x580x110,
                    14: BOX_size.DIM_1345x580x110,
                    15: BOX_size.DIM_1345x580x110,
                },
            },
        },

        MODULAR: {
            SISTEMA: {
                BASE: {
                    2: BOX_size.DIM_600x620x110,
                    3: BOX_size.DIM_600x620x110,
                    4: BOX_size.DIM_600x620x110,
                    5: BOX_size.DIM_600x620x110,
                    6: BOX_size.DIM_600x620x110,
                    7: BOX_size.DIM_600x620x110,
                    8: BOX_size.DIM_800x620x110,
                    9: BOX_size.DIM_800x620x110,
                    10: BOX_size.DIM_800x620x110,
                    11: BOX_size.DIM_800x620x110,
                    12: BOX_size.DIM_800x620x110,
                    13: BOX_size.DIM_1000x620x110,
                    14: BOX_size.DIM_1000x620x110,
                    15: BOX_size.DIM_1000x620x110,
                    16: BOX_size.DIM_1000x620x110,
                },
                KIT: {
                    2: BOX_size.DIM_600x620x110,
                    3: BOX_size.DIM_600x620x110,
                    4: BOX_size.DIM_600x620x110,
                    5: BOX_size.DIM_600x620x110,
                    6: BOX_size.DIM_600x620x110,
                    7: BOX_size.DIM_800x620x110,
                    8: BOX_size.DIM_800x620x110,
                    9: BOX_size.DIM_800x620x110,
                    10: BOX_size.DIM_800x620x110,
                    11: BOX_size.DIM_1000x620x110,
                    12: BOX_size.DIM_1000x620x110,
                    13: BOX_size.DIM_1000x620x110,
                    14: BOX_size.DIM_800x620x110,
                    15: BOX_size.DIM_1200x620x110,
                    16: BOX_size.DIM_1200x620x110,
                },
                AUTO: {
                    2: BOX_size.DIM_600x620x110,
                    3: BOX_size.DIM_600x620x110,
                    4: BOX_size.DIM_600x620x110,
                    5: BOX_size.DIM_600x620x110,
                    6: BOX_size.DIM_800x620x110,
                    7: BOX_size.DIM_800x620x110,
                    8: BOX_size.DIM_800x620x110,
                    9: BOX_size.DIM_800x620x110,
                    10: BOX_size.DIM_1000x620x110,
                    11: BOX_size.DIM_1000x620x110,
                    12: BOX_size.DIM_1000x620x110,
                    13: BOX_size.DIM_1000x620x110,
                    14: BOX_size.DIM_1200x620x110,
                    15: BOX_size.DIM_1200x620x110,
                    16: BOX_size.DIM_1200x620x110,
                },
                KIT_AUTO: {
                    2: BOX_size.DIM_600x620x110,
                    3: BOX_size.DIM_600x620x110,
                    4: BOX_size.DIM_800x620x110,
                    5: BOX_size.DIM_800x620x110,
                    6: BOX_size.DIM_800x620x110,
                    7: BOX_size.DIM_800x620x110,
                    8: BOX_size.DIM_1000x620x110,
                    9: BOX_size.DIM_1000x620x110,
                    10: BOX_size.DIM_1000x620x110,
                    11: BOX_size.DIM_1000x620x110,
                    12: BOX_size.DIM_1200x620x110,
                    13: BOX_size.DIM_1200x620x110,
                    14: BOX_size.DIM_1200x620x110,
                    15: BOX_size.DIM_1200x620x110,
                    16: BOX_size.DIM_1300x620x110,
                },
            },
            SP: {
                BASE: {
                    2: BOX_size.DIM_340x580x110,
                    3: BOX_size.DIM_440x580x110,
                    4: BOX_size.DIM_440x580x110,
                    5: BOX_size.DIM_580x580x110,
                    6: BOX_size.DIM_580x580x110,
                    7: BOX_size.DIM_580x580x110,
                    8: BOX_size.DIM_720x580x110,
                    9: BOX_size.DIM_720x580x110,
                    10: BOX_size.DIM_720x580x110,
                    11: BOX_size.DIM_780x580x110,
                    12: BOX_size.DIM_970x580x110,
                    13: BOX_size.DIM_970x580x110,
                    14: BOX_size.DIM_970x580x110,
                    15: BOX_size.DIM_1145x580x110,
                    16: BOX_size.DIM_1145x580x110,
                },
                KIT: {
                    2: BOX_size.DIM_440x580x110,
                    3: BOX_size.DIM_580x580x110,
                    4: BOX_size.DIM_580x580x110,
                    5: BOX_size.DIM_580x580x110,
                    6: BOX_size.DIM_720x580x110,
                    7: BOX_size.DIM_720x580x110,
                    8: BOX_size.DIM_720x580x110,
                    9: BOX_size.DIM_780x580x110,
                    10: BOX_size.DIM_780x580x110,
                    11: BOX_size.DIM_970x580x110,
                    12: BOX_size.DIM_970x580x110,
                    13: BOX_size.DIM_970x580x110,
                    14: BOX_size.DIM_1145x580x110,
                    15: BOX_size.DIM_1145x580x110,
                    16: BOX_size.DIM_1345x580x110,
                },
                AUTO: {
                    2: BOX_size.DIM_580x580x110,
                    3: BOX_size.DIM_580x580x110,
                    4: BOX_size.DIM_580x580x110,
                    5: BOX_size.DIM_720x580x110,
                    6: BOX_size.DIM_720x580x110,
                    7: BOX_size.DIM_720x580x110,
                    8: BOX_size.DIM_780x580x110,
                    9: BOX_size.DIM_970x580x110,
                    10: BOX_size.DIM_970x580x110,
                    11: BOX_size.DIM_970x580x110,
                    12: BOX_size.DIM_1145x580x110,
                    13: BOX_size.DIM_1145x580x110,
                    14: BOX_size.DIM_1145x580x110,
                    15: BOX_size.DIM_1345x580x110,
                    16: BOX_size.DIM_1345x580x110,
                },
                KIT_AUTO: {
                    2: BOX_size.DIM_580x580x110,
                    3: BOX_size.DIM_720x580x110,
                    4: BOX_size.DIM_720x580x110,
                    5: BOX_size.DIM_720x580x110,
                    6: BOX_size.DIM_780x580x110,
                    7: BOX_size.DIM_780x580x110,
                    8: BOX_size.DIM_970x580x110,
                    9: BOX_size.DIM_970x580x110,
                    10: BOX_size.DIM_970x580x110,
                    11: BOX_size.DIM_1145x580x110,
                    12: BOX_size.DIM_1145x580x110,
                    13: BOX_size.DIM_1145x580x110,
                    14: BOX_size.DIM_1345x580x110,
                    15: BOX_size.DIM_1345x580x110,
                    16: BOX_size.DIM_1345x580x110,
                },
            },
        },
    };

    const distributorKind = isModular ? "MODULAR" : "METALLIC";
    const groupedBoxes = new Map();

    for (const dist of distributionReqs) {
        const circuits = dist.size;
        const count = dist.quantity || 1;
        const selected =
            BOX_MAP[distributorKind]?.[boxSystem]?.[mode]?.[circuits] ||
            BOX_MAP[distributorKind]?.[boxSystem]?.BASE?.[circuits];

        if (!selected) {
            continue;
        }

        //  Use selected object as key string (unique by box size name)
        const key =
            selected.name ||
            `${selected.width}x${selected.height}x${selected.depth}`;
        groupedBoxes.set(key, {
            size: selected,
            type: IPA_PRODUCT_TYPES.BOX,
            subtype: metalBox,
            quantity: (groupedBoxes.get(key)?.quantity || 0) + count,
        });
    }

    const results = Array.from(groupedBoxes.values());

    return results;
}

export function createTytanFoamRequirements(boxReqs, insulationReqs, unitId) {
    // Total of boxes (sum quantitconsoy from boxReqs)
    const totalBoxes = boxReqs.reduce((sum, b) => sum + (b.quantity || 0), 0);

    let piecesByInsulation = 0;
    insulationReqs.forEach(req => {
        if (req.size < 100) {
            if (
                req.subtype === INSULATION_VARIANTS.TACKER ||
                req.subtype === INSULATION_VARIANTS.ADDITIONAL_TACKER
            ) {
                if (req.size === TACKER_PANEL_SIZE.THK_25) {
                    piecesByInsulation += req.quantity / 50;
                } else {
                    piecesByInsulation += req.quantity / 7.5;
                }
            } else {
                piecesByInsulation += req.quantity / 10;
            }
        }
    });

    return [
        {
            type: IPA_PRODUCT_TYPES.ACCESSORY,
            subtype: ACCESSORY_VARIANTS.FOAM_TYTAN,
            size: ACCESSORY_SIZE.DEFAULT,
            quantity: Math.ceil(totalBoxes + piecesByInsulation),
            unitId: unitId,
        },
    ];
}

/**
 * PIPE requirements builder (per pipe type).
 *
 * Rules:
 *  - Quantities must be expressed in meters, not rolls.
 *  - Full rolls are 500m (16mm, 17mm, PE-RT), 600m (17mm 600m), or 400m (20mm).
 *  - Round up remainders to the next roll but return total meters.
 *  - For 16/17: always ≥1×500 when there is pipe; small remainder in 240m rolls.
 *    If remainder would need 2+ ×240 rolls (rem > 240), prefer another 500m roll.
 *
 * Examples:
 *   totalPipe = 955.30 → 2×500 = 1000m total
 *   totalPipe = 1200 → 2×500 + 1×240 = 1240m total
 *
 * @param {Object} params
 * @param {Array} params.rooms - Room list containing total_pipe.
 * @param {number} params.pipeDiameter - Pipe diameter type (16, 17, 20, 116, 117).
 * @returns {Array<{type,subtype,size,quantity}>} Requirements in meters.
 */
export function createPipeRequirements({ rooms, pipeDiameter }) {
	if (!Array.isArray(rooms) || rooms.length === 0 || !pipeDiameter) {
		return [];
	}

	// --- 1) Sum total pipe length across all rooms
	const totalPipe = rooms.reduce(
		(sum, r) => sum + Number(r.total_pipe || 0),
		0
	);

	if (!totalPipe) {
		return [];
	}

	const reqs = [];

	const add = (sizeEnum, meters) => {
		reqs.push({
			type: IPA_PRODUCT_TYPES.PIPE,
			subtype: pipeDiameter,
			size: sizeEnum,
			quantity: meters, // always meters
		});
	};

	// Split Ø16/17 into 500m rolls + optional 240m remainder (raw qty; rounding later).
	const add500And240 = (size500, size240) => {
		let use500 = Math.floor(totalPipe / 500);
		if (use500 < 1) use500 = 1; // at least 1×500 when there is pipe
		let rem = totalPipe - use500 * 500;
		// rem > 240 ⇒ ceil(rem/240) ≥ 2 ⇒ prefer one more 500m roll over 2–3×240
		if (rem > 240) {
			use500 += 1;
			rem = totalPipe - use500 * 500;
		}
		add(size500, use500 * 500);
		if (rem > 0) {
			add(size240, rem); // Raw remainder, rounding happens later
		}
	};

	// --- 2) Split pipe into appropriate roll sizes (raw quantities, no rounding)
	if (pipeDiameter === 16) {
		add500And240(PIPE_SIZE.DIM_16x2_0_500M, PIPE_SIZE.DIM_16x2_0_240M);
	} else if (pipeDiameter === 17) {
		add500And240(PIPE_SIZE.DIM_17x2_0_500M, PIPE_SIZE.DIM_17X2_0_240M);
	} else if (pipeDiameter === 117) {
		// PE-Xa 17 (600m) — same split logic as 17/500m, with 600m rolls
		const full600 = Math.floor(totalPipe / 600);
		const use600 = full600 >= 1 ? full600 : 1; // at least 1 roll of 600 when there is pipe
		const rem = totalPipe - use600 * 600; // remainder after 600m rolls (can be negative)
		add(PIPE_SIZE.DIM_17x2_0_600M, use600 * 600);
		if (rem > 0) {
			// 240m remainder product is catalogued under diameter 17
			reqs.push({
				type: IPA_PRODUCT_TYPES.PIPE,
				subtype: 17,
				size: PIPE_SIZE.DIM_17X2_0_240M,
				quantity: rem,
			});
		}
	} else if (pipeDiameter === 20) {
		add(PIPE_SIZE.DIM_20x2_0_400M, totalPipe); // Raw total, rounding happens later
	} else if (pipeDiameter === 116) {
		add(PIPE_SIZE.DIM_16X2_1_500M, totalPipe); // Raw total, rounding happens later
	}

	return reqs;
}

// Helper to normalize UI choices (some come as {value:{...}}, others directly as {...})
const normalizeChoice = x =>
    x && typeof x === "object" && "value" in x ? x.value : x;

/**
 * ADDITIONAL INSULATION (TACKER) requirements builder.
 *
 * Calculates additional insulation (array) the same way as Tacker plates.
 *
 * Logic:
 * - Each room may have multiple additional insulation layers (array).
 * - For every layer with subtype=TACKER, we add that room’s area to the total for that thickness.
 * - Quantities are rounded up to full pack multiples.
 *
 * Fix:
 * - When compartmentation > 0, rooms might not contain their own `additionalInsulations` array.
 *   In that case, we fallback to the location-level additionalInsulations (shared across all rooms).
 *
 * Rules:
 *   qty(m²) = ceil(area / pack_m2) * pack_m2
 *
 * Default pack sizes (m² per pack):
 *   - 20mm → 12.5
 *   - 30mm → 8
 *   - 50mm → 5
 *
 * Notes:
 * - If the same thickness appears multiple times, the area is counted multiple times (intended).
 * - Output matches the IPA standard format: {type, subtype, size, quantity}.
 */
export function createAdditionalInsulationRequirements({
    rooms,
    packByThickness = {
        [TACKER_PANEL_SIZE.THK_20]: 12.5,
        [TACKER_PANEL_SIZE.THK_30]: 8,
        [TACKER_PANEL_SIZE.THK_50]: 5,
    },
}) {
    if (!Array.isArray(rooms) || rooms.length === 0) {
        return [];
    }

    // Fallback: use location-level additionalInsulations if rooms don't have their own
    const locationAdditional =
        rooms?.[0]?.location?.additionalInsulations ?? [];

    const areaByThickness = {}; // {20: m², 30: m², 50: m²}

    rooms.forEach(r => {
        const area =
            typeof r?.area === "number" ? r.area : Number(r?.area?.sq_m || 0);

        // Use room-specific array if present, otherwise fallback to location-level one
        const arr = Array.isArray(r?.additionalInsulations)
            ? r.additionalInsulations
            : locationAdditional;

        if (!area || arr.length === 0) return;

        arr.forEach(item => {
            const v = normalizeChoice(item);
            if (!v) return;
            if (v.type !== IPA_PRODUCT_TYPES.INSULATION) return;
            if (
                v.subtype !== INSULATION_VARIANTS.TACKER &&
                v.subtype !== INSULATION_VARIANTS.ADDITIONAL_TACKER
            )
                return;

            const size = Number(v.size);
            areaByThickness[size] = (areaByThickness[size] || 0) + area;
        });
    });

    const out = [];

    Object.entries(areaByThickness).forEach(([sizeStr, totalArea]) => {
        const size = Number(sizeStr);
        const pack = packByThickness[size] || 0;
        if (!pack) {
            return;
        }
        const qtyM2 = totalArea; // Return raw area (rounding happens in roundRequirements)

        out.push({
            type: IPA_PRODUCT_TYPES.INSULATION,
            subtype: INSULATION_VARIANTS.ADDITIONAL_TACKER,
            size,
            quantity: qtyM2,
        });
    });

    return out;
}

/**
 * PERIMETER BAND (501.01.10)
 *
 * Always included.
 * One requirement per unit.
 * Quantity = total area of that unit (m²), rounded up to next multiple of 25 m.
 *
 * Example:
 *   area = 132.7 → ceil(132.7 / 25) * 25 = 150 m
 */
export function createPerimeterBandRequirements({ rooms }) {
    if (!Array.isArray(rooms) || rooms.length === 0) {
        return [];
    }

    // Get all areas per unitId
    const areasByUnit = {};
    for (const r of rooms) {
        const unitId = Number(r?.unitId) || 0;
        const area =
            typeof r?.area === "number" ? r.area : Number(r?.area?.sq_m || 0);
        if (!area) continue;

        if (!areasByUnit[unitId]) areasByUnit[unitId] = [];
        areasByUnit[unitId].push(area);
    }

    const reqs = [];

    for (const [unitIdStr, areas] of Object.entries(areasByUnit)) {
        let qty = 0;

        for (const area of areas) {
            qty += Math.sqrt(area) * 4;
        }

        // Return raw quantity (rounding happens in roundRequirements)
        // qty is already calculated as sqrt(area) * 4

        reqs.push({
            type: IPA_PRODUCT_TYPES.ACCESSORY,
            subtype: ACCESSORY_VARIANTS.PERIMETER_BAND,
            size: ACCESSORY_SIZE.DEFAULT,
            quantity: qty,
            unitId: Number(unitIdStr),
        });
    }

    return reqs;
}

/**
 * ADDITIVE THERMOFLUID (502.01.11)
 *
 * Rule (legacy parity):
 *   qty(l) = ceil( area * 0.06 * 3 / 10 ) * 10
 *   → ceil( area * 0.018 / 10 ) * 10
 *
 * One additive per unit.
 * Area = total m² for that unit.
 */
export function createAdditiveThermofluidRequirements({ rooms }) {
    if (!Array.isArray(rooms) || rooms.length === 0) {
        return [];
    }

    // Group total area per unitId
    const areaByUnit = {};
    for (const r of rooms) {
        const unitId = Number(r?.unitId) || 0;
        const area =
            typeof r?.area === "number" ? r.area : Number(r?.area?.sq_m || 0);
        if (!area) continue;

        if (!areaByUnit[unitId]) areaByUnit[unitId] = 0;
        areaByUnit[unitId] += area;
    }

    const reqs = [];

    for (const [unitIdStr, area] of Object.entries(areaByUnit)) {
        // legacy formula - return raw quantity (rounding happens in roundRequirements)
        const qty = area * 0.06 * 3;

        reqs.push({
            type: IPA_PRODUCT_TYPES.ACCESSORY,
            subtype: ACCESSORY_VARIANTS.ADDITIVE_THERMOFLUID,
            size: ACCESSORY_SIZE.DEFAULT,
            quantity: qty,
            unitId: Number(unitIdStr),
        });
    }

    return reqs;
}

/**
 * Water volume per meter of pipe (L/m) by diameter.
 * PE-Xa 16 & PE-RT 16 = 0.113; PE-Xa 17 = 0.133; PE-Xa 20 = 0.201.
 */
const PIPE_WATER_VOLUME_PER_METER = {
	16: 0.113,
	116: 0.113, // PE-RT 16
	17: 0.133,
	117: 0.133, // PE-Xa 17 (600m)
	20: 0.201,
};

/** 1 L additive (1 buc 502.05.12) per this many litres of installation water. */
const ADDITIVE_PER_WATER_LITRES = 100;

/** Standard water volume in inertial/buffer (L). Applied once per unit in roundRequirements. */
const INERTIAL_WATER_LITRES = 60;

/**
 * ADDITIVE ANTI-ALGAE (502.05.12)
 *
 * Formula:
 *   1. Total pipe water = sum(pipe length per room) × volume_per_meter(pipeDiameter).
 *   2. Total installation water = pipe water + 60 L (inertial).
 *   3. Additive qty = ceil(total_water / 100) buc (1 L per 100 L water, rounded up).
 *
 * Volume per meter (L/m): 16 & 116 → 0.113; 17 → 0.133; 20 → 0.201.
 */
export function createAdditiveAntiAlgaeRequirements({ rooms, pipeDiameter }) {
    if (!Array.isArray(rooms) || rooms.length === 0) {
        return [];
    }

    const volumePerMeter =
        PIPE_WATER_VOLUME_PER_METER[pipeDiameter] ??
        PIPE_WATER_VOLUME_PER_METER[16];
    let totalPipeMeters = 0;
    for (const r of rooms) {
        const pipeMeters =
            typeof r?.total_pipe === "number"
                ? r.total_pipe
                : Number(r?.total_pipe?.sq_m || 0);
        if (pipeMeters) {
            totalPipeMeters += pipeMeters;
        }
    }

    if (!totalPipeMeters) {
        return [];
    }

    // Pipe water volume in litres (no inertial here; inertial is applied once per unit in UI layer)
    const pipeWaterLitres = totalPipeMeters * volumePerMeter;
    // Raw qty in pieces (1 buc per 100L of pipe water); inertial offset is added before rounding
    const qty = pipeWaterLitres / ADDITIVE_PER_WATER_LITRES;

    return [
        {
            type: IPA_PRODUCT_TYPES.ACCESSORY,
            subtype: ACCESSORY_VARIANTS.ADDITIVE_ANTI_ALGAE,
            size: ACCESSORY_SIZE.DEFAULT,
            quantity: qty,
        },
    ];
}

// ============================================================================
// MAIN BUILDER
// ============================================================================

/**
 * Orchestrates all IPA requirement builders.
 *
 * Pipeline:
 *   1) Distribution manifold (+ Box, Temp control, Automation) — unchanged
 *   2) GROOVED insulation + GROOVED clips
 *   3) TACKER plates + TACKER roll (surface m²)
 *   4) TACKER foil (SIMPLE 110/55, ALU 50) — legacy parity
 *   5) TACKER clips (only if any TACKER is present)
 *
 * @param {Object} params
 * @param {Array} params.rooms - Enriched rooms with all configuration fields
 * @param {boolean} [params.unitScope=false] - Whether calculations should be unit-scoped
 * @returns {Array<{type,subtype,size,quantity}>} Raw (unrounded) requirements
 */
export function calcIpaRequirements({
    rooms,
    unitScope = false,
    excludePipe = false,
    pipeRooms = null,
}) {
    resetAlgaeLimit();
    // Extract config from first enriched room (all rooms in array have same config)
    const firstRoom =
        Array.isArray(rooms) && rooms.length > 0 ? rooms[0] : null;

    if (!firstRoom) {
        return [];
    }

    // Extract all configuration from enriched room
    const distributionType = firstRoom.distributionType;
    const tempControlKit = firstRoom.tempControlKit;
    const automation = firstRoom.automation;
    const automationSelection = firstRoom.automationSelection ?? [];
    const pipeDiameter = firstRoom.pipeDiameter;
    const metalBox = firstRoom.metalBox;
    const reinforcementMesh = firstRoom.reinforcementMesh;
    let requirements = [];

    // 1) Distribution manifold — UNCHANGED
    const circuitCount = Array.isArray(rooms)
        ? rooms.reduce((acc, room) => {
              // dacă avem unitScope, numără doar camerele din același unitId
              if (unitScope) {
                  const currentUnit = rooms[0]?.unitId ?? null;
                  // Only count rooms that have the same unitId (including null/undefined)
                  // This ensures that when compartimentation is 0, all rooms are counted together
                  const roomUnitId = room?.unitId ?? null;
                  if (roomUnitId === currentUnit) {
                      return acc + (Number(room.circuit_no) || 0);
                  }
                  return acc;
              }
              return acc + (Number(room.circuit_no) || 0);
          }, 0)
        : 0;

    const distReqs = createDistributionRequirements({
        distributionType,
        totalCircuits: circuitCount,
        rooms,
    });
    requirements.push(...distReqs);
    // 1.1  Distribution Accessories

    const numDistributors = distReqs.reduce(
        (sum, r) => sum + (r.quantity || 1),
        0,
    );
    // 1.1) Distribution main products
    requirements.push(
        ...createDistributionMainProductsRequirements({
            distributionType,
            pipeDiameter,
            distributionReqs: distReqs, // fără distSummary
        }),
    );
    // 2) Box — UNCHANGED
    const boxReqs = createBoxesRequirements({
        distributionType,
        tempControlKit,
        automation,
        metalBox,
        distributionReqs: distReqs,
    });
    requirements.push(...boxReqs);

    // 3) Temperature control — UPDATED
    if (tempControlKit && tempControlKit !== 1) {
        const tempReqs = createTemperatureControlRequirements({
            tempControlKit,
            numDistributors,
        });

        requirements.push(...tempReqs);

        requirements = requirements.filter(
            r =>
                !(
                    r.type === IPA_PRODUCT_TYPES.DISTRIBUTION_ACCESSORIES &&
                    (r.subtype === DIST_ACCESSORY_VARIANTS.VALVE ||
                        r.subtype === DIST_ACCESSORY_VARIANTS.VALVE_RR)
                ),
        );
    }

    // 4) Automation — COMPLET
    if (automation > 1 && circuitCount > 0) {
        const autoReqs = createAutomationRequirements({
            automation,
            circuitCount,
            numDistributors,
        });

        // 1) Adăugăm produsele CALCULATE (doar qty > 0)
        for (const item of autoReqs) {
            if (item.quantity > 0) {
                requirements.push(item);
            }
        }

        // 2) Adăugăm produsele selectate manual în dialog
        for (const m of automationSelection ?? []) {
            if (Number(m.quantity) > 0) {
                requirements.push({
                    type: IPA_PRODUCT_TYPES.AUTOMATION,
                    subtype: m.subtype,
                    size: m.size,
                    quantity: Number(m.quantity),
                });
            }
        }
    }

    // 5) GROOVED insulation (+clips) — NEW ADD, OTHERS UNCHANGED
    const groovedReq = createGroovedInsulationRequirements({ rooms });
    requirements.push(...groovedReq);

    // 5.5) DRY insulation
    const dryReq = createDryInsulationRequirements({ rooms });
    requirements.push(...dryReq);

    // 6) TACKER
    const tackerPlates = createTackerPlateRequirements({ rooms });
    const tackerRoll = createTackerRollRequirements({ rooms });
    const hasTacker = tackerPlates.length || tackerRoll.length;
    requirements.push(...tackerPlates);
    requirements.push(...tackerRoll);
    const foilReqs = createTackerFoilRequirementsOld({ rooms });

    requirements.push(...foilReqs);

    // 6.1) ADDITIONAL INSULATION
    requirements.push(...createAdditionalInsulationRequirements({ rooms }));

    // 7) PIPE — use pipeRooms when provided (unit scope), else rooms; skip when excludePipe
    if (!excludePipe) {
        const roomsForPipe =
            Array.isArray(pipeRooms) && pipeRooms.length ? pipeRooms : rooms;
        const pipeReqs = createPipeRequirements({
            rooms: roomsForPipe,
            pipeDiameter: roomsForPipe[0]?.pipeDiameter ?? pipeDiameter,
        });
        requirements.push(...pipeReqs);
    }

    // 8) Perimetral Band
    requirements.push(...createPerimeterBandRequirements({ rooms }));

    // 9) fluid additive (502.01.11) — excluded when insulation is "EPS - Sistem uscat" (DRY)
    const hasDryInsulation = (rooms || []).some(room => {
        const ins =
            room?.insulationType ??
            room?.insulation_type ??
            room?.insulationType?.value ??
            room?.insulation_type?.value;
        return (
            ins?.type === IPA_PRODUCT_TYPES.INSULATION &&
            ins?.subtype === INSULATION_VARIANTS.DRY
        );
    });
    if (!hasDryInsulation) {
        requirements.push(...createAdditiveThermofluidRequirements({ rooms }));
    }

    // 10) algae additive
    requirements.push(
        ...createAdditiveAntiAlgaeRequirements({ rooms, pipeDiameter }),
    );
    // 11) Reinforcement Mesh
    requirements.push(
        ...createReinforcementMeshRequirements({
            rooms,
            reinforcementMesh, // passed from unit/global config
        }),
    );

    // 12) Clips Tacker 40mm
    requirements.push(
        ...createTackerClipsRequirements(
            {
                rooms,
                hasTackerInsulation: !!hasTacker,
            },
            requirements.filter(
                req => req.type === IPA_PRODUCT_TYPES.REINFORCEMENT_MESH,
            ),
        ),
    );

    // Return raw (unrounded) requirements - rounding will be applied when needed
    return requirements;
}

// ============================================================================
// ROUNDING UTILITIES
// ============================================================================

/**
 * Determines the rounding rule for a requirement based on its type/subtype/size
 *
 * @param {Object} req - Requirement object with type, subtype, size
 * @param {Object} context - Additional context (pack sizes, etc.)
 * @returns {Object|null} Rounding rule or null if no rounding needed
 */
function getRoundingRule(req, context = {}) {
    const { type, subtype, size } = req;

    // Distribution, Box, Automation, Temperature Control - no rounding (exact counts)
    if (
        [
            IPA_PRODUCT_TYPES.DISTRIBUTION,
            IPA_PRODUCT_TYPES.DISTRIBUTION_ACCESSORIES,
            IPA_PRODUCT_TYPES.DISTRIBUTION_MAIN_PRODUCTS,
            IPA_PRODUCT_TYPES.BOX,
            IPA_PRODUCT_TYPES.AUTOMATION,
            IPA_PRODUCT_TYPES.TEMPERATURE_CONTROL,
        ].includes(type)
    ) {
        return null; // No rounding for these
    }

    // GROOVED insulation - base packs + 3% waste
    if (
        type === IPA_PRODUCT_TYPES.INSULATION &&
        subtype === INSULATION_VARIANTS.GROOVED &&
        size !== INSULATION_CLIPS.GROOVED_CLIPS
    ) {
        const packSizes = {
            //packing size necessary for 101.01.102, 101.02.102, 101.03.102
            // [GROOVED_PANEL_SIZE.DIM_10MM]: 0.85,
            // [GROOVED_PANEL_SIZE.DIM_20MM]: 0.85,
            // [GROOVED_PANEL_SIZE.DIM_30MM]: 0.85,

            //packing size necessary for 101.01.51, 101.02.51, 101.03.51
            [GROOVED_PANEL_SIZE.DIM_10MM]: 1.12,
            [GROOVED_PANEL_SIZE.DIM_20MM]: 1.12,
            [GROOVED_PANEL_SIZE.DIM_30MM]: 1.12,
            [GROOVED_PANEL_SIZE.DIM_SYSTEM_REDUCED]: 1.12,
        };
        const packSize = packSizes[size] || context.groovedPackSize || 0.85;
        return {
            type: "multiple_with_separate_waste",
            packSize,
            wasteFactor: 0.03,
        };
    }

    // TACKER plates - round up to pack multiple
    // Also handles ADDITIONAL_TACKER (same rounding rules as TACKER)
    if (
        type === IPA_PRODUCT_TYPES.INSULATION &&
        (subtype === INSULATION_VARIANTS.TACKER ||
            subtype === INSULATION_VARIANTS.ADDITIONAL_TACKER)
    ) {
        // Check if it's a plate (not foil roll)
        if (
            ![
                TACKER_FOIL_ROLL.SIMPLE_110,
                TACKER_FOIL_ROLL.SIMPLE_55,
                TACKER_FOIL_ROLL.ALU_50,
            ].includes(size)
        ) {
            const packSizes = {
                [TACKER_PANEL_SIZE.THK_20]: 12.5,
                [TACKER_PANEL_SIZE.THK_25]: 10,
                [TACKER_PANEL_SIZE.THK_30]: 8,
                [TACKER_PANEL_SIZE.THK_50]: 5,
            };
            const packSize = packSizes[size] || context.tackerPackSize || 10;
            return {
                type: "multiple",
                packSize,
            };
        }
    }

    // TACKER foil rolls
    if (
        type === IPA_PRODUCT_TYPES.INSULATION &&
        subtype === INSULATION_VARIANTS.TACKER
    ) {
        if (size === TACKER_FOIL_ROLL.SIMPLE_110) {
            return {
                type: "multiple",
                packSize: 110,
            };
        }
        if (size === TACKER_FOIL_ROLL.SIMPLE_55) {
            return {
                type: "multiple",
                packSize: 55,
            };
        }
        if (size === TACKER_FOIL_ROLL.ALU_50) {
            return {
                type: "multiple",
                packSize: 50,
            };
        }
    }

    // DRY insulation - matches Python formula:
    // 1. Round area to pack multiple: ceil(area / packSize) * packSize
    // 2. Add 2% waste: quantity + quantity * 0.02
    // 3. Convert to packs: ceil((quantity + waste) / packSize)
    // Result is in packs, not m²
    if (
        type === IPA_PRODUCT_TYPES.INSULATION &&
        subtype === INSULATION_VARIANTS.DRY
    ) {
        const packSize = context.dryPackSize || 0.72;
        return {
            type: "multiple_dry_system",
            packSize,
            wasteFactor: 0.02,
        };
    }

	// PIPE - round up to roll sizes
	if (type === IPA_PRODUCT_TYPES.PIPE) {
		// Pipe requirements are already split by size, so we round each size separately
		if (
			size === PIPE_SIZE.DIM_16x2_0_240M ||
			size === PIPE_SIZE.DIM_17X2_0_240M
		) {
			return {
				type: "multiple",
				packSize: 240,
			};
		}
		if (size === PIPE_SIZE.DIM_20x2_0_400M) {
			return {
				type: "multiple",
				packSize: 400,
			};
		}
		if (
			size === PIPE_SIZE.DIM_16x2_0_500M ||
			size === PIPE_SIZE.DIM_17x2_0_500M ||
			size === PIPE_SIZE.DIM_16X2_1_500M
		) {
			return {
				type: "multiple",
				packSize: 500,
			};
		}
		if (size === PIPE_SIZE.DIM_17x2_0_600M) {
			return {
				type: "multiple",
				packSize: 600,
			};
		}
	}

    // PERIMETER BAND - round up to 25m multiple
    if (
        type === IPA_PRODUCT_TYPES.ACCESSORY &&
        subtype === ACCESSORY_VARIANTS.PERIMETER_BAND
    ) {
        return {
            type: "multiple",
            packSize: 25,
        };
    }

    // ADDITIVE THERMOFLUID - round up to 10ml multiple
    if (
        type === IPA_PRODUCT_TYPES.ACCESSORY &&
        subtype === ACCESSORY_VARIANTS.ADDITIVE_THERMOFLUID
    ) {
        return {
            type: "multiple",
            packSize: 10,
        };
    }

    // ADDITIVE ANTI-ALGAE - round up (1 buc per 100 L water)
    if (
        type === IPA_PRODUCT_TYPES.ACCESSORY &&
        subtype === ACCESSORY_VARIANTS.ADDITIVE_ANTI_ALGAE
    ) {
        return {
            type: "ceil",
        };
    }

    // REINFORCEMENT MESH - round up to 50 m² multiple
    if (type === IPA_PRODUCT_TYPES.REINFORCEMENT_MESH) {
        return {
            type: "multiple",
            packSize: 50,
        };
    }

    // CLIPS/STAPLES - round up
    // Note: grooved clips have subtype=GROOVED and size=GROOVED_CLIPS
    // tacker staples have subtype=TACKER and size=TACKER_STAPLES
    if (type === IPA_PRODUCT_TYPES.INSULATION) {
        if (
            size === INSULATION_CLIPS.GROOVED_CLIPS ||
            size === INSULATION_CLIPS.TACKER_STAPLES
        ) {
            return {
                type: "ceil",
            };
        }
    }

    // FOAM TYTAN - no rounding (exact count)
    if (
        type === IPA_PRODUCT_TYPES.ACCESSORY &&
        subtype === ACCESSORY_VARIANTS.FOAM_TYTAN
    ) {
        return null;
    }

    // Default: no rounding
    return null;
}

// @TODO Fix when formula is received for algae additives
let algaeLimit = 0;

export function resetAlgaeLimit() {
    algaeLimit = 0;
}

/**
 * Applies a rounding rule to a quantity
 *
 * @param {number} rawQuantity - Raw (unrounded) quantity
 * @param {Object} roundingRule - Rounding rule
 * @returns {number} Rounded quantity
 */
function applyRoundingRule(rawQuantity, roundingRule) {
    if (!roundingRule || rawQuantity <= 0) return rawQuantity;

    switch (roundingRule.type) {
        case "ceil":
            return Math.ceil(rawQuantity);

        case "ceil_plus_one":
            if (!algaeLimit) {
                algaeLimit++;
                return Math.ceil(rawQuantity) + 1;
            } else {
                return Math.ceil(rawQuantity);
            }

        case "floor":
            return Math.floor(rawQuantity);

        case "round":
            return Math.round(rawQuantity);

        case "multiple": {
            const packSize = roundingRule.packSize || 1;
            if (roundingRule.wasteFactor) {
                // Apply waste factor first, then round to pack multiple
                const withWaste = rawQuantity * (1 + roundingRule.wasteFactor);
                return Math.ceil(withWaste / packSize) * packSize;
            }
            return Math.ceil(rawQuantity / packSize) * packSize;
        }

        case "multiple_with_separate_waste": {
            // For grooved insulation: base packs + extra packs (3% waste)
            // Calculate base packs first, then calculate waste as percentage of base packs
            // This avoids double rounding of the division operation
            const basePackSize = roundingRule.packSize || 1;
            const wasteFactor = roundingRule.wasteFactor || 0.03;

            // Calculate base packs needed (rounded up)
            const basePacks = Math.ceil(rawQuantity / basePackSize);

            // Calculate waste packs as percentage of base packs (rounded up)
            // This ensures waste is calculated from the rounded base packs, not from raw quantity
            const extraPacks = Math.ceil(basePacks * wasteFactor);

            // Total quantity = (base packs + waste packs) * pack size
            return (basePacks + extraPacks) * basePackSize;
        }

        case "multiple_dry_system": {
            // Matches Python formula for dry system exactly:
            // 1. Round area to pack multiple: ceil(area / packSize) * packSize
            // 2. Add 2% waste: quantity + quantity * 0.02
            // 3. Convert to packs: ceil((quantity + waste) / packSize)
            // rawQuantity is in m², result is in packs (matching Python behavior)
            const packSize = roundingRule.packSize || 0.72;
            const wasteFactor = roundingRule.wasteFactor || 0.02;

            // Step 1: Round area to pack multiple (gives m²)
            const roundedArea = Math.ceil(rawQuantity / packSize) * packSize;

            // Step 2: Add 2% waste (still in m²)
            const withWaste = roundedArea + roundedArea * wasteFactor;

            // Step 3: Convert to packs and round up (result is in packs, like Python)
            return Math.ceil(withWaste / packSize);
        }

        default:
            return rawQuantity;
    }
}

/**
 * Rounds a single requirement
 *
 * @param {Object} req - Requirement with raw quantity
 * @param {Object} context - Additional context for rounding rules
 * @returns {Object} Requirement with rounded quantity
 */
export function roundRequirement(req, context = {}) {
    if (!req || req.quantity === undefined) return req;

    const roundingRule = getRoundingRule(req, context);
    if (!roundingRule) return req; // No rounding needed

    return {
        ...req,
        quantity: applyRoundingRule(req.quantity, roundingRule),
    };
}

/**
 * Rounds an array of requirements
 *
 * @param {Array} requirements - Array of requirements with raw quantities
 * @param {Object} context - Additional context for rounding rules
 * @param {Array} [context.rooms] - All rooms (needed for grooved clips calculation)
 * @param {boolean} [context.addGroovedClips=true] - Whether to add grooved clips if missing
 * @returns {Array} Requirements with rounded quantities
 */
export function roundRequirements(requirements, context = {}) {
    if (!Array.isArray(requirements)) return requirements;
    // Track which requirements have been rounded (to avoid double rounding)
    const roundedIndices = new Set();

    let groovedClipsReqs = null;
    let roundedMeshReq = null;
    let hasGroovedClips = false;

    // Add grooved clips if needed (before final rounding)
    const shouldAddGroovedClips =
        context.addGroovedClips !== false && context.rooms;
    if (shouldAddGroovedClips) {
        // Check if grooved clips are already present
        hasGroovedClips = requirements.some(
            req =>
                req.type === IPA_PRODUCT_TYPES.INSULATION &&
                req.subtype === INSULATION_VARIANTS.GROOVED &&
                req.size === INSULATION_CLIPS.GROOVED_CLIPS,
        );

        if (!hasGroovedClips) {
            // Get reinforcement mesh requirements and their indices
            const meshReqsWithIndices = requirements
                .map((req, idx) => ({ req, idx }))
                .filter(
                    ({ req }) =>
                        req.type === IPA_PRODUCT_TYPES.REINFORCEMENT_MESH,
                );

            // Merge mesh requirements first (in case there are multiple)
            // Sum all mesh quantities into a single requirement
            const totalMeshArea = meshReqsWithIndices.reduce(
                (sum, { req }) => sum + (req.quantity || 0),
                0,
            );
            const mergedMeshReq =
                meshReqsWithIndices.length > 0
                    ? {
                          ...meshReqsWithIndices[0].req,
                          quantity: totalMeshArea,
                      }
                    : null;

            // Round the merged mesh requirement FIRST (before calculating clips)
            // This is critical: clips need to know the rounded mesh quantity
            roundedMeshReq = mergedMeshReq
                ? roundRequirement(mergedMeshReq, context)
                : null;

            // DEBUG: Before calculating clips
            // Note: groovedInsulationReqs was removed as it's not used
            // We use rooms directly to calculate grooved area (raw, before rounding)

            // Update all mesh requirements in the array to the rounded value
            // (they should all be the same after merging)
            // Mark them as already rounded to avoid double rounding
            if (roundedMeshReq && meshReqsWithIndices.length) {
                const firstIdx = meshReqsWithIndices[0].idx;

                // 1) pune merged+rounded pe primul
                roundedIndices.add(firstIdx);
                requirements[firstIdx] = { ...roundedMeshReq };

                // 2) șterge restul mesh-urilor (din coadă ca să nu strici indexii)
                const extraIdxs = meshReqsWithIndices
                    .slice(1)
                    .map(x => x.idx)
                    .sort((a, b) => b - a);
                extraIdxs.forEach(idx => requirements.splice(idx, 1));
            }

            // Calculate grooved clips using insulation area and ROUNDED mesh requirement
            // Always use rooms directly to calculate grooved area (raw, before rounding)
            // Don't use groovedInsulationReqs as they might be rounded or transformed

            const groovedInsulationReqs = requirements.filter(
                req =>
                    req.type === IPA_PRODUCT_TYPES.INSULATION &&
                    req.subtype === INSULATION_VARIANTS.GROOVED &&
                    req.size !== INSULATION_CLIPS.GROOVED_CLIPS, // Exclude clips themselves
            );

            groovedClipsReqs = createGroovedClipsRequirements(
                {
                    rooms: context.rooms, // Use rooms directly to get raw area (always raw, never rounded)
                    groovedInsulation: groovedInsulationReqs, // Don't use requirements - they might be rounded
                },
                roundedMeshReq ? [roundedMeshReq] : [],
            );

            // Check if grooved clips already exist in requirements (shouldn't happen, but check anyway)
            const existingClips = requirements.filter(
                req =>
                    req.type === IPA_PRODUCT_TYPES.INSULATION &&
                    req.subtype === INSULATION_VARIANTS.GROOVED &&
                    req.size === INSULATION_CLIPS.GROOVED_CLIPS,
            );

            if (existingClips.length > 0) {
                if (typeof console !== "undefined") {
                    console.warn(
                        "[DEBUG roundRequirements] WARNING: Grooved clips already exist in requirements!",
                        {
                            existingClips: existingClips.map(c => ({
                                quantity: c.quantity,
                            })),
                            newClips: groovedClipsReqs.map(c => ({
                                quantity: c.quantity,
                            })),
                        },
                    );
                }
                // Don't add duplicates - this would cause double counting
                // Just update the existing ones or skip adding
            } else {
                // Add grooved clips to requirements (will be rounded below)
                const unitId = context.rooms?.[0]?.unitId;

                requirements.push(
                    ...groovedClipsReqs.map(r => ({
                        ...r,
                        ...(unitId != null ? { unitId } : {}),
                    })),
                );
            }
        }
    }

    // ------------------------------------------------------------------------
    // ADDITIVE ANTI-ALGAE: merge all raw quantities and add 60 L inertial once
    // (so personalization/unit-scoped calls do not double-count inertial).
    // ------------------------------------------------------------------------
    const algaeReqsWithIndices = requirements
        .map((req, idx) => ({ req, idx }))
        .filter(
            ({ req }) =>
                req.type === IPA_PRODUCT_TYPES.ACCESSORY &&
                req.subtype === ACCESSORY_VARIANTS.ADDITIVE_ANTI_ALGAE,
        );
    if (algaeReqsWithIndices.length > 0) {
        const totalPipeWaterQty = algaeReqsWithIndices.reduce(
            (sum, { req }) => sum + (Number(req.quantity ?? 0) || 0),
            0,
        );
        const mergedQty =
            totalPipeWaterQty +
            INERTIAL_WATER_LITRES / ADDITIVE_PER_WATER_LITRES;
        const first = algaeReqsWithIndices[0];
        requirements[first.idx] = {
            ...first.req,
            quantity: mergedQty,
        };
        const extraIdxs = algaeReqsWithIndices
            .slice(1)
            .map(x => x.idx)
            .sort((a, b) => b - a);
        extraIdxs.forEach(idx => requirements.splice(idx, 1));
    }

    // ------------------------------------------------------------------------
    // TACKER FOIL legacy rounding/splitting (110/55 for SIMPLE, 50 for ALU)
    //
    // Why this exists:
    // - We often get a single "SIMPLE_110" requirement with raw m².
    // - Generic "multiple 110" rounding turns an exact 55m² need into 110m².
    // - Legacy behavior is to allow ordering 55m² (SIMPLE_55) when applicable.
    //
    // We normalize foil requirements here BEFORE applying generic rounding rules:
    // - SIMPLE: round to 55m², then split into (110k) + 55 when remainder is 55.
    // - ALU: round to 50m².
    // ------------------------------------------------------------------------
    const normalizedForFoil = [];
    const isTackerFoilSize = size =>
        [
            TACKER_FOIL_ROLL.SIMPLE_110,
            TACKER_FOIL_ROLL.SIMPLE_55,
            TACKER_FOIL_ROLL.ALU_50,
        ].includes(size);

    for (const req of requirements) {
        const isTackerFoil =
            req?.type === IPA_PRODUCT_TYPES.INSULATION &&
            req?.subtype === INSULATION_VARIANTS.TACKER &&
            isTackerFoilSize(req?.size);

        if (!isTackerFoil) {
            normalizedForFoil.push(req);
            continue;
        }

        const rawQty = Number(req.quantity ?? 0) || 0;
        if (!rawQty) continue;

        // SIMPLE foil: accept either SIMPLE_110 or SIMPLE_55 as input.
        if (
            req.size === TACKER_FOIL_ROLL.SIMPLE_110 ||
            req.size === TACKER_FOIL_ROLL.SIMPLE_55
        ) {
            const t55 = Math.max(55, Math.ceil(rawQty / 55) * 55);

            if (t55 % 110 === 55) {
                const qty110 = Math.floor(t55 / 110) * 110;
                if (qty110 > 0) {
                    normalizedForFoil.push({
                        ...req,
                        size: TACKER_FOIL_ROLL.SIMPLE_110,
                        quantity: qty110,
                    });
                }
                normalizedForFoil.push({
                    ...req,
                    size: TACKER_FOIL_ROLL.SIMPLE_55,
                    quantity: 55,
                });
            } else {
                normalizedForFoil.push({
                    ...req,
                    size: TACKER_FOIL_ROLL.SIMPLE_110,
                    quantity: Math.ceil(t55 / 110) * 110,
                });
            }
            continue;
        }

        // ALU foil: 50m² roll
        if (req.size === TACKER_FOIL_ROLL.ALU_50) {
            normalizedForFoil.push({
                ...req,
                quantity: Math.ceil(rawQty / 50) * 50,
            });
            continue;
        }

        // Fallback (should not happen)
        normalizedForFoil.push(req);
    }

    // Replace working array after foil normalization
    requirements = normalizedForFoil;

    // Round all requirements, but skip those that were already rounded
    const roundedReqs = requirements.map((req, idx) => {
        if (roundedIndices.has(idx)) {
            // This requirement was already rounded (e.g., mesh), return as-is
            return req;
        }
        return roundRequirement(req, context);
    });

    // Create foam tytan requirements

    const boxesReqs = requirements.filter(
        req => req.type === IPA_PRODUCT_TYPES.BOX,
    );

    const insulationReqs = roundedReqs.filter(
        req => req.type === IPA_PRODUCT_TYPES.INSULATION,
    );

    const tytanFoam = createTytanFoamRequirements(
        boxesReqs,
        insulationReqs,
        context.unitId,
    );

    roundedReqs.push(...tytanFoam);

    return roundedReqs;
}
